/**
 * EQUIPMENT FINDER RANKING — the Duty-First Finder's candidate builder.
 *
 * Merges the three evidence sources (Equipment BOM, OEM register, equipment
 * catalog) into ONE deduplicated candidate list and ranks it. Ranking is
 * honest by construction:
 *  - REAL envelopes rank on the exact solver screens the workbench already
 *    computed (`GenerationEquipmentScreenRow`) — never re-derived numbers.
 *  - Full prefills rank on an exact solver screen ONLY when their heat-rate
 *    basis is explicitly published (LHV/HHV). A derived or unstated basis is
 *    never assumed for a ranking — those candidates sit in a labeled
 *    "confirm basis to rank" band instead.
 *  - Partial/insufficient candidates are never ranked; they close the list
 *    with the reason stated.
 *
 * Pure + deterministic: same inputs → same order.
 */
import type {
  GenerationImpactScenarioInput,
  GenerationUserEquipmentEntry,
} from '../../types/generationImpact';
import type {
  OemGenerationProduct,
  OemGenerationSourceRegister,
} from '../../types/oemGenerationCatalog';
import type { BomItem, EquipmentCatalogEntry, SupplyChainPlan } from '../../types/supplyChain';
import {
  evaluateGridFrequencyFit,
  type SiteGridFrequencyHz,
} from './gridFrequency';
import {
  listGenerationBomImportCandidates,
  resolveBomItemImport,
  resolveEquipmentCatalogEntryImport,
  resolveOemProductImport,
  type GenerationEquipmentImportResolution,
  type GenerationUserEquipmentPrefill,
} from './equipmentImportSources';
import {
  GENERATION_EQUIPMENT_CATALOG,
  type GenerationEquipmentScreenRow,
} from './generationEquipmentCatalog';
import { OEM_GENERATION_SOURCE_REGISTER } from './generationOemSourceRegister';
import {
  applyGenerationUserEquipmentEnvelope,
  validateGenerationUserEquipmentEntry,
} from './userEquipmentEnvelope';
import { solveGenerationImpact } from './solveGenerationImpact';
import {
  deriveGenerationEquipmentConstraintFits,
  type GenerationEquipmentConstraintFit,
} from './workflowGuidance';

export type FinderSource = 'bom' | 'oem' | 'catalog';
export type FinderRankMode = 'best-fit' | 'mw' | 'footprint' | 'heat-rate' | 'lead-time';

/** Best-fit band the candidate lands in (renders as sections). */
export type FinderBand = 'ranked' | 'confirm-basis' | 'needs-data';

export interface FinderExactScreen {
  clearsDuty: boolean;
  clearsReserve: boolean;
  siteNetMWPerBlock: number;
  fits: readonly GenerationEquipmentConstraintFit[];
  exceedsCount: number;
  undocumentedCount: number;
}

export interface EquipmentFinderCandidate {
  /** Stable identity used for dedup and testids. */
  key: string;
  source: FinderSource;
  title: string;
  subtitle: string | null;
  resolution: GenerationEquipmentImportResolution;
  band: FinderBand;
  /** Present only when an exact solver screen backs the ranking. */
  exact: FinderExactScreen | null;
  /** One-sentence honest explanation of the rank or the gap. */
  why: string;
  stats: {
    ratedMWPerUnit: number | null;
    ratedScopeLabel: string | null;
    heatRateKjPerKwh: number | null;
    heatRateLabel: string | null;
    footprintAreaM2: number | null;
    footprintLabel: string | null;
    leadTimeMonthsLow: number | null;
    leadTimeMonthsHigh: number | null;
  };
}

const fmt = (value: number, digits = 0): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(value);

function fitCounts(fits: readonly GenerationEquipmentConstraintFit[]): {
  exceedsCount: number; undocumentedCount: number;
} {
  return {
    exceedsCount: fits.filter(fit => fit.status === 'exceeds').length,
    undocumentedCount: fits.filter(fit => fit.status === 'undocumented').length,
  };
}

function exactFromResult(
  input: GenerationImpactScenarioInput,
  result: GenerationEquipmentScreenRow['result'],
  siteNetMWPerBlock: number,
): FinderExactScreen {
  const capacity = result.capacity;
  const fits = deriveGenerationEquipmentConstraintFits(result, input);
  return {
    clearsDuty: (capacity.contingencyVerdict ?? capacity.nMinusOneVerdict) === 'pass',
    clearsReserve: (capacity.contingencyReserveVerdict ?? capacity.nMinusOneReserveVerdict) === 'pass',
    siteNetMWPerBlock,
    fits,
    ...fitCounts(fits),
  };
}

function whyForExact(exact: FinderExactScreen, criterionLabel: string): string {
  const duty = exact.clearsReserve
    ? `Clears ${criterionLabel} + reserve in the exact solver screen`
    : exact.clearsDuty
      ? `Clears the ${criterionLabel} duty but not the reserve criterion`
      : `Falls short of the ${criterionLabel} duty at the current block count`;
  const exceeded = exact.fits.filter(fit => fit.status === 'exceeds');
  const limits = exceeded.length > 0
    ? ` — exceeds the documented ${exceeded.map(fit => fit.label.toLowerCase()).join(' and ')} limit${exceeded.length === 1 ? '' : 's'} (flagged, not hidden)`
    : exact.undocumentedCount > 0
      ? ` — some site-envelope limits remain undocumented`
      : ` — fits every documented site-envelope limit`;
  return `${duty}${limits}.`;
}

/** Build a transient (never persisted) user entry from a full prefill so the
 *  exact solver can screen it. Only called when the basis is explicit. */
function transientEntryFromPrefill(
  key: string,
  prefill: GenerationUserEquipmentPrefill,
): GenerationUserEquipmentEntry | null {
  if (prefill.heatRateValueKjPerKwh == null
    || (prefill.heatingValueBasis !== 'LHV' && prefill.heatingValueBasis !== 'HHV')
    || prefill.ratedOutputMWPerBlock == null
    || prefill.ratingScope == null
    || prefill.technology == null) return null;
  const entry: GenerationUserEquipmentEntry = {
    id: `finder-${key}`,
    manufacturer: prefill.manufacturer ?? 'Unknown',
    model: prefill.model ?? key,
    trainConfiguration: prefill.trainConfiguration ?? 'user-defined',
    technology: prefill.technology,
    unitsPerBlock: prefill.unitsPerBlock ?? 1,
    ratedOutputMWPerBlock: prefill.ratedOutputMWPerBlock,
    ratingScope: prefill.ratingScope,
    heatRateValue: prefill.heatRateValueKjPerKwh,
    heatRateUnit: 'kJ/kWh',
    heatingValueBasis: prefill.heatingValueBasis,
    // The Add-equipment dialog's disclosed screening defaults — what the
    // prefill would produce on submit, so the ranking previews that exact entry.
    auxiliaryFractionOfIsoGross: 0.04,
    ambientFactor: 0.9,
    degradationFactor: 0.98,
    createdAt: '1970-01-01T00:00:00.000Z',
  };
  return validateGenerationUserEquipmentEntry(entry).length === 0 ? entry : null;
}

function screenTransientEntry(
  input: GenerationImpactScenarioInput,
  entry: GenerationUserEquipmentEntry,
): FinderExactScreen | null {
  try {
    const seeded = structuredClone(input);
    seeded.equipmentWorkspace = {
      shortlistedCatalogIds: [],
      userEquipment: [entry],
    };
    const applied = applyGenerationUserEquipmentEnvelope(seeded, entry.id);
    const result = solveGenerationImpact(applied);
    return exactFromResult(input, result, result.unitResults[0]?.netMWPerUnit ?? 0);
  } catch {
    return null;
  }
}

function ratingScopeLabel(scope: string | null | undefined): string | null {
  if (scope == null) return null;
  return scope.replaceAll('-', ' ');
}

function productStats(product: OemGenerationProduct, prefill?: GenerationUserEquipmentPrefill): EquipmentFinderCandidate['stats'] {
  const physical = product.physicalReferences[0] ?? null;
  const heatRateValue = prefill?.heatRateValueKjPerKwh ?? null;
  const heatRateLabel = heatRateValue == null
    ? null
    : `${fmt(heatRateValue)} kJ/kWh ${prefill?.heatRateDerived ? '· derived' : prefill?.heatingValueBasis ?? 'basis?'}`;
  return {
    ratedMWPerUnit: prefill?.ratedOutputMWPerBlock ?? null,
    ratedScopeLabel: ratingScopeLabel(prefill?.ratingScope ?? null),
    heatRateKjPerKwh: heatRateValue,
    heatRateLabel,
    footprintAreaM2: physical ? physical.lengthM * physical.widthM : null,
    footprintLabel: physical ? `${fmt(physical.lengthM, 1)} × ${fmt(physical.widthM, 1)} m` : null,
    leadTimeMonthsLow: null,
    leadTimeMonthsHigh: null,
  };
}

function envelopeStats(catalogId: string): EquipmentFinderCandidate['stats'] {
  const entry = GENERATION_EQUIPMENT_CATALOG.find(candidate => candidate.catalogId === catalogId);
  if (!entry) {
    return {
      ratedMWPerUnit: null, ratedScopeLabel: null, heatRateKjPerKwh: null, heatRateLabel: null,
      footprintAreaM2: null, footprintLabel: null, leadTimeMonthsLow: null, leadTimeMonthsHigh: null,
    };
  }
  const physical = entry.publishedPhysicalReference;
  const heatRate = entry.publishedRating.heatRateValue;
  return {
    ratedMWPerUnit: entry.publishedRating.outputMWPerBlock,
    ratedScopeLabel: ratingScopeLabel(entry.publishedRating.ratingScope),
    heatRateKjPerKwh: heatRate != null && entry.publishedRating.heatRateUnit === 'kJ/kWh' ? heatRate : null,
    heatRateLabel: heatRate == null
      ? null
      : `${fmt(heatRate)} ${entry.publishedRating.heatRateUnit ?? ''} ${entry.publishedRating.heatingValueBasis ?? ''}`.trim(),
    footprintAreaM2: physical ? physical.lengthM * physical.widthM : null,
    footprintLabel: physical ? `${fmt(physical.lengthM, 1)} × ${fmt(physical.widthM, 1)} m` : null,
    leadTimeMonthsLow: null,
    leadTimeMonthsHigh: null,
  };
}

export interface BuildEquipmentFinderCandidatesArgs {
  input: GenerationImpactScenarioInput;
  criterionLabel: string;
  plan?: SupplyChainPlan;
  register?: OemGenerationSourceRegister;
  /** Resolved async by the dialog; candidates rebuild when it arrives. */
  catalogEntries?: readonly EquipmentCatalogEntry[];
  /** The admitted screening rows the workbench already solved (tier-a exacts). */
  equipmentRows: readonly GenerationEquipmentScreenRow[];
  /** Site grid frequency; null/omitted = unknown, mismatch flags suppressed. */
  siteGridFrequencyHz?: SiteGridFrequencyHz | null;
}

export function buildEquipmentFinderCandidates(
  args: BuildEquipmentFinderCandidatesArgs,
): readonly EquipmentFinderCandidate[] {
  const register = args.register ?? OEM_GENERATION_SOURCE_REGISTER;
  const rowByCatalogId = new Map(args.equipmentRows.map(row => [row.catalogId, row] as const));
  const seen = new Set<string>();
  const candidates: EquipmentFinderCandidate[] = [];

  const identityOf = (resolution: GenerationEquipmentImportResolution, fallback: string): string => {
    if (resolution.tier === 'catalog-envelope') return `env:${resolution.catalogId}`;
    if (resolution.tier === 'prefill-full' || resolution.tier === 'prefill-partial') {
      const source = resolution.prefill.importSource;
      return source.oemProductId != null ? `prod:${source.oemProductId}` : `id:${fallback}`;
    }
    return `id:${fallback}`;
  };

  const push = (candidate: EquipmentFinderCandidate, identity: string): void => {
    if (seen.has(identity)) return;
    seen.add(identity);
    candidates.push(candidate);
  };

  const siteHz = args.siteGridFrequencyHz ?? null;
  const resolverFrequencyOption = siteHz != null ? { siteGridFrequencyHz: siteHz } : {};
  /** Append a compact mismatch clause so the ranked card itself carries it. */
  const withFrequencyWhy = (
    resolution: GenerationEquipmentImportResolution,
    why: string,
  ): string => {
    if (siteHz == null) return why;
    if (resolution.tier !== 'prefill-full' && resolution.tier !== 'prefill-partial') return why;
    const fit = evaluateGridFrequencyFit(resolution.prefill.frequencyHz, siteHz);
    if (fit !== 'incompatible') return why;
    const published = [...(resolution.prefill.frequencyHz ?? [])].join('/');
    return `${why} Grid-frequency mismatch: ${published} Hz case vs ${siteHz} Hz site — select the ${siteHz} Hz equivalent.`;
  };

  const bandAndWhy = (
    resolution: GenerationEquipmentImportResolution,
    exact: FinderExactScreen | null,
  ): { band: FinderBand; why: string } => {
    if (exact) return { band: 'ranked', why: whyForExact(exact, args.criterionLabel) };
    if (resolution.tier === 'prefill-full') {
      return {
        band: 'confirm-basis',
        why: 'Published performance is complete, but the heating-value basis is not stated — confirm LHV/HHV in the prefill form before this can be ranked by the exact solver.',
      };
    }
    if (resolution.tier === 'prefill-partial') {
      return {
        band: 'needs-data',
        why: `Unranked — missing ${resolution.missing.join(' and ')}. Prefill what exists and complete it, or send the OEM data request.`,
      };
    }
    return {
      band: 'needs-data',
      why: resolution.tier === 'insufficient'
        ? `Unranked — ${resolution.reasons.join(' ')}`
        : 'Unranked.',
    };
  };

  // ---- 1. Equipment BOM lines (highest provenance priority) ---------------
  for (const item of listGenerationBomImportCandidates(args.plan)) {
    const resolution = resolveBomItemImport(item, { register, ...resolverFrequencyOption });
    const identity = identityOf(resolution, `bom:${item.id}`);
    let exact: FinderExactScreen | null = null;
    let stats: EquipmentFinderCandidate['stats'] = {
      ratedMWPerUnit: item.powerPerUnitMw ?? null,
      ratedScopeLabel: item.powerPerUnitMw != null ? 'net per unit' : null,
      heatRateKjPerKwh: null,
      heatRateLabel: null,
      footprintAreaM2: null,
      footprintLabel: null,
      leadTimeMonthsLow: item.leadTimeMonthsLow ?? null,
      leadTimeMonthsHigh: item.leadTimeMonthsHigh ?? null,
    };
    if (resolution.tier === 'catalog-envelope') {
      const row = rowByCatalogId.get(resolution.catalogId);
      if (row) exact = exactFromResult(args.input, row.result, row.siteNetMWPerBlock);
      stats = { ...envelopeStats(resolution.catalogId), leadTimeMonthsLow: item.leadTimeMonthsLow ?? null, leadTimeMonthsHigh: item.leadTimeMonthsHigh ?? null };
    } else if (resolution.tier === 'prefill-full') {
      const entry = transientEntryFromPrefill(`bom-${item.id}`, resolution.prefill);
      if (entry) exact = screenTransientEntry(args.input, entry);
      stats = {
        ...stats,
        ratedMWPerUnit: resolution.prefill.ratedOutputMWPerBlock ?? stats.ratedMWPerUnit,
        ratedScopeLabel: ratingScopeLabel(resolution.prefill.ratingScope ?? null) ?? stats.ratedScopeLabel,
        heatRateKjPerKwh: resolution.prefill.heatRateValueKjPerKwh ?? null,
        heatRateLabel: resolution.prefill.heatRateValueKjPerKwh != null
          ? `${fmt(resolution.prefill.heatRateValueKjPerKwh)} kJ/kWh ${resolution.prefill.heatRateDerived ? '· derived' : resolution.prefill.heatingValueBasis ?? 'basis?'}`
          : null,
      };
    }
    const { band, why: baseWhy } = bandAndWhy(resolution, exact);
    const why = withFrequencyWhy(resolution, baseWhy);
    const snapshot = item.oemGenerationBasis;
    push({
      key: `bom-${item.id}`,
      source: 'bom',
      title: snapshot ? `${snapshot.manufacturer} · ${snapshot.model}` : item.name,
      subtitle: `Your BOM · qty ${item.quantity} · ${item.status}`,
      resolution,
      band,
      exact,
      why,
      stats,
    }, identity);
  }

  // ---- 2. OEM register products -------------------------------------------
  for (const product of register.products) {
    const resolution = resolveOemProductImport(product.id, { register, ...resolverFrequencyOption });
    const identity = identityOf(resolution, `prod:${product.id}`);
    let exact: FinderExactScreen | null = null;
    let stats: EquipmentFinderCandidate['stats'];
    if (resolution.tier === 'catalog-envelope') {
      const row = rowByCatalogId.get(resolution.catalogId);
      if (row) exact = exactFromResult(args.input, row.result, row.siteNetMWPerBlock);
      stats = envelopeStats(resolution.catalogId);
    } else if (resolution.tier === 'prefill-full' || resolution.tier === 'prefill-partial') {
      if (resolution.tier === 'prefill-full') {
        const entry = transientEntryFromPrefill(product.id, resolution.prefill);
        if (entry) exact = screenTransientEntry(args.input, entry);
      }
      stats = productStats(product, resolution.prefill);
    } else {
      stats = productStats(product);
    }
    const { band, why: baseWhy } = bandAndWhy(resolution, exact);
    const why = withFrequencyWhy(resolution, baseWhy);
    push({
      key: product.id,
      source: 'oem',
      title: `${product.manufacturer} · ${product.model}`,
      subtitle: product.variant || null,
      resolution,
      band,
      exact,
      why,
      stats,
    }, identity);
  }

  // ---- 3. Equipment catalog rows (dedup against register mirrors) ---------
  for (const entry of args.catalogEntries ?? []) {
    const generationRelevant = entry.scmCategory === 'power-generation'
      || entry.oemGenerationProductId != null
      || entry.generationScreeningId != null;
    if (!generationRelevant) continue;
    const resolution = resolveEquipmentCatalogEntryImport(entry, { register, ...resolverFrequencyOption });
    const identity = identityOf(resolution, `cat:${entry.id}`);
    let exact: FinderExactScreen | null = null;
    if (resolution.tier === 'catalog-envelope') {
      const row = rowByCatalogId.get(resolution.catalogId);
      if (row) exact = exactFromResult(args.input, row.result, row.siteNetMWPerBlock);
    }
    const { band, why: baseWhy } = bandAndWhy(resolution, exact);
    const why = withFrequencyWhy(resolution, baseWhy);
    push({
      key: `catalog-${entry.id}`,
      source: 'catalog',
      title: entry.name,
      subtitle: `Company catalog · ${entry.source}`,
      resolution,
      band,
      exact,
      why,
      stats: {
        ratedMWPerUnit: entry.powerPerUnitMw ?? null,
        ratedScopeLabel: entry.powerPerUnitMw != null ? 'net per unit' : null,
        heatRateKjPerKwh: null,
        heatRateLabel: null,
        footprintAreaM2: null,
        footprintLabel: null,
        leadTimeMonthsLow: entry.leadTimeMonths?.low ?? null,
        leadTimeMonthsHigh: entry.leadTimeMonths?.high ?? null,
      },
    }, identity);
  }

  return candidates;
}

const BAND_ORDER: Record<FinderBand, number> = { ranked: 0, 'confirm-basis': 1, 'needs-data': 2 };

function byMetricAscNullsLast(
  metric: (candidate: EquipmentFinderCandidate) => number | null,
  descending = false,
) {
  return (left: EquipmentFinderCandidate, right: EquipmentFinderCandidate): number => {
    const a = metric(left);
    const b = metric(right);
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return descending ? b - a : a - b;
  };
}

/** Deterministic rank. `best-fit` keeps the bands; metric modes flatten with
 *  missing metrics last. Ties preserve build order (stable sort). */
export function rankEquipmentFinderCandidates(
  candidates: readonly EquipmentFinderCandidate[],
  mode: FinderRankMode,
): readonly EquipmentFinderCandidate[] {
  const list = [...candidates];
  if (mode === 'best-fit') {
    return list.sort((left, right) => {
      const band = BAND_ORDER[left.band] - BAND_ORDER[right.band];
      if (band !== 0) return band;
      const a = left.exact;
      const b = right.exact;
      if (a && b) {
        return Number(b.clearsReserve) - Number(a.clearsReserve)
          || Number(b.clearsDuty) - Number(a.clearsDuty)
          || a.exceedsCount - b.exceedsCount
          || a.undocumentedCount - b.undocumentedCount
          || a.siteNetMWPerBlock - b.siteNetMWPerBlock;
      }
      return 0;
    });
  }
  if (mode === 'mw') return list.sort(byMetricAscNullsLast(candidate => candidate.stats.ratedMWPerUnit, true));
  if (mode === 'footprint') return list.sort(byMetricAscNullsLast(candidate => candidate.stats.footprintAreaM2));
  if (mode === 'heat-rate') return list.sort(byMetricAscNullsLast(candidate => candidate.stats.heatRateKjPerKwh));
  return list.sort(byMetricAscNullsLast(candidate => candidate.stats.leadTimeMonthsLow));
}
